import type { DatesToSearch } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';

import type {
  DestinationAvailabilitySearchUserCriteria,
  ItinerarySearchUserCriteria,
} from '@libs/trip-planner-interfaces';
import { LOCAL_SESSION_ID } from '@libs/search-service-smart-search';

import type { HotelIngredient } from '../../recipe/ingredients/hotelIngredient';
import {
  adaptHotelIngredientToDestinationAvailabilitySearchCriteria,
  getDestinationIngredientConstraintSelection,
} from '../ingredientAdapters/destinationsAvailabilitySearchCriteria/hotelIngredientDestinationAvailabilitySearchAdapter';
import type { OwnStayIngredient } from '../../recipe/ingredients/ownStayIngredient';

export interface DestinationAvailabilityRecipe {
  itineraryCriteria: ItinerarySearchUserCriteria;
  destinationIngredient: HotelIngredient | OwnStayIngredient;
  searchActivities: boolean;
  datesToSearch: DatesToSearch;
}

export function adaptRecipeToDestinationAvailabilitySearchCriteria(
  recipe: DestinationAvailabilityRecipe
): DestinationAvailabilitySearchUserCriteria | undefined {
  const {
    itineraryCriteria,
    destinationIngredient,
    searchActivities,
    datesToSearch,
  } = recipe;

  const ingredientIndex = itineraryCriteria.ingredients?.findIndex(
    (ingredient) =>
      ingredient.ingredientId === destinationIngredient.ingredientId
  );

  if (ingredientIndex === -1 || ingredientIndex === undefined) {
    return;
  }

  const adaptedDestinationHotelIngredient =
    adaptHotelIngredientToDestinationAvailabilitySearchCriteria(
      destinationIngredient,
      searchActivities
    );

  const destinationAvailabilityIngredients = itineraryCriteria.ingredients
    ? [
        ...itineraryCriteria.ingredients.slice(0, ingredientIndex),
        adaptedDestinationHotelIngredient,
        ...itineraryCriteria.ingredients.slice(ingredientIndex + 1),
      ]
    : null;

  const destinationAvailabilityRecipe: ItinerarySearchUserCriteria = {
    ingredients: destinationAvailabilityIngredients,
    pax: searchActivities ? [{ numberOfAdults: 1 }] : itineraryCriteria.pax,
    startSearchDate: datesToSearch.startDate,
    endSearchDate: datesToSearch.endDate,
    sessionId: LOCAL_SESSION_ID,
  };

  return {
    recipe: destinationAvailabilityRecipe,
    ingredientIndex,
    constraint: getDestinationIngredientConstraintSelection(
      destinationIngredient
    ),
    day: datesToSearch.selectedDate,
  };
}
